import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { DataEntity } from '../../types/data';
import { Routes } from '../../enums';
import styles from './toc.module.css';

export type TocProps = {
  data: DataEntity[];
  expandedNodes: string[];
  onToggle: (nodeId: string) => void;
};

export default function Toc({ data, expandedNodes, onToggle }: TocProps) {
  const [shownMobileToc, toggleMobileToc] = useState(false);

  const compare = (a: DataEntity, b: DataEntity) => {
    // Sort by order
    // If the first item has a higher number, move it down
    // If the first item has a lower number, move it up
    if (a.sort < b.sort) return -1;
    if (a.sort > b.sort) return 1;

    // If the votes number is the same between both items, sort alphabetically
    // If the first item comes first in the alphabet, move it up
    // Otherwise move it down
    if (a.title > a.title) return 1;
    if (a.title < a.title) return -1;

    return 0;
  };

  const renderEntity = (entity: DataEntity, level = 0) => {
    const hasChildren = !!(entity.children && entity.children.length);
    const isExpanded = expandedNodes.includes(entity.path);

    return (
      <div key={entity.path} className={styles.listItem}>
        <div className={styles.entity} data-level={level}>
          {hasChildren ? (
            <div className={styles.toggle} onClick={() => onToggle(entity.path)}>
              <div className={styles.entityIcon}>
                {isExpanded ? (
                  <span role="button" className="fa fa-chevron-down" />
                ) : (
                  <span role="button" className="fa fa-chevron-right" />
                )}
              </div>
              <div className={styles.entityLink}>{entity.title}</div>
            </div>
          ) : (
            <NavLink
              className={styles.entityLink}
              activeClassName={styles.entityLink_active}
              exact
              to={entity.path}
              onClick={() => toggleMobileToc(false)}
            >
              {entity.title} {entity.isNew ? <div className={styles.entityLinkNew}></div> : null}
            </NavLink>
          )}
        </div>
        <div className={styles.entityChildren} data-expand={isExpanded}>
          {hasChildren &&
            entity.children &&
            entity.children.sort(compare).map((child) => renderEntity(child, level + 1))}
        </div>
      </div>
    );
  };

  if (!data.length) return null;

  return (
    <React.Fragment>
      <div className={styles.head} data-mobile>
        <Link className={styles.mobileMenuHome} to={Routes.Reports}>
          Demos
        </Link>
        <div
          className={`${styles.mobileMenuToggle} ${shownMobileToc ? styles.mobileMenuToggle_opened : ''}`}
          onClick={() => toggleMobileToc(!shownMobileToc)}
        >
          <svg width="16" height="16" focusable="false" viewBox="0 0 32 32">
            <path d="M16,22.5c-0.3,0-0.7-0.1-0.9-0.3l-11-9c-0.6-0.5-0.7-1.5-0.2-2.1s1.5-0.7,2.1-0.2l10,8.2l10-8.2c0.6-0.5,1.6-0.4,2.1,0.2c0.5,0.6,0.4,1.6-0.2,2.1l-11,9C16.7,22.4,16.3,22.5,16,22.5z" />
          </svg>
        </div>
      </div>
      <div className={`${styles.root} ${shownMobileToc ? styles.root_showMobile : ''}`}>
        <div className={styles.tree}>
          <div className={styles.container}>
            <div className={styles.list}>{data.sort(compare).map((entity) => renderEntity(entity))}</div>
            <div className={styles.border} data-desktop />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
